import FeatureGates from "@atlaskit/feature-gate-js-client";
import { useConfig } from "@atlassiansox/bxp-content-framework";

import { getCookie } from "../../utils";

// https://trello.atlassian.net/browse/TJC-9006
export const TRELLO_ADVANCED_COBRANDING_URL_GATE =
    "trello_advanced_cobranding_url";

// https://switcheroo.atlassian.com/ui/gates/8f4bbbe2-19d6-4f12-aa68-581ea69c4d17/key/trello_anon_id_gate
export const TRELLO_ANON_ID_GATE = "trello_anon_id_gate";

type UrlParams = {
    confirmReturnUrl?: string;
    email?: string;
    prompt?: string;
    returnUrl?: string;
    isLogin?: boolean;
};

const getAnonId = (isEnabled: boolean): string | undefined => {
    if (!isEnabled) {
        return undefined;
    }
    try {
        return getCookie("ajs_anonymous_id")?.replace(/^"|"$/g, "");
    } catch {
        return undefined;
    }
};

export const useIdentityUrl = ({
    confirmReturnUrl,
    email,
    prompt,
    returnUrl,
    isLogin
}: UrlParams = {}): string => {
    const { environment } = useConfig();
    const isProduction = environment === "production";

    const baseUrl = isProduction
        ? "id.atlassian.com"
        : "id.stg.internal.atlassian.com";
    const host = isProduction ? "trello.com" : "trellis.coffee";

    const isAdvancedCobrandingEnabled = FeatureGates.checkGate(
        TRELLO_ADVANCED_COBRANDING_URL_GATE
    );

    const isTrelloAnonIdEnabled = FeatureGates.checkGate(TRELLO_ANON_ID_GATE);

    // Param passed twice to Identity (in primary URL and encoded in continue/callback URL)
    // See https://hello.atlassian.net/browse/BXPT-1348?focusedCommentId=8509655
    // Omitted for trello--direct-signup (advanced co-branding) — see TJC-9006
    const display = "eyJ2ZXJpZmljYXRpb25TdHJhdGVneSI6InNvZnQifQ==";

    const trelloAnonId = getAnonId(isTrelloAnonIdEnabled);

    const continueParams = new URLSearchParams();
    if (!isAdvancedCobrandingEnabled) {
        continueParams.set("display", display);
    }

    if (returnUrl) {
        continueParams.append("returnUrl", returnUrl);
    }

    if (confirmReturnUrl) {
        continueParams.append("confirmReturnUrl", confirmReturnUrl);
    }

    if (!isLogin) {
        continueParams.append("createMember", "true");
    }

    const application = isAdvancedCobrandingEnabled
        ? "trello--direct-signup"
        : "trello";

    const continueQuery = continueParams.toString();
    const continueValue = continueQuery
        ? `https://${host}/auth/atlassian/callback?${continueQuery}`
        : `https://${host}/auth/atlassian/callback`;

    const params = new URLSearchParams({
        application,
        continue: continueValue
    });
    if (!isAdvancedCobrandingEnabled) {
        params.append("display", display);
    }

    if (email) {
        params.append("login_hint", email);
    }

    if (prompt) {
        params.append("prompt", prompt);
    }

    if (trelloAnonId) {
        params.append("anonId", trelloAnonId);
    }

    const action = isLogin ? "login" : "signup";

    return `https://${baseUrl}/${action}?${params}`;
};
